import { memo, useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { VenueFeatureCollection, VenueMapView } from "@/types/venues";
import { normalizeUnknownValue } from "@/lib/utils";
import { CLUSTER_COLOR, CLUSTER_RADIUS, HEATMAP_PAINT, PIN_COLOR, PIN_RADIUS, PIN_STROKE } from "./pinStyles";

const HEAT_LAYER_ID = "venue-heatmap";
const CLUSTER_LAYER_ID = "venue-clusters";
const CLUSTER_COUNT_LAYER_ID = "venue-cluster-count";
const PIN_LAYER_ID = "venue-pins";
const MAP_STYLE =
  "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";

const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283];

const getBoundsFromCollection = (collection: VenueFeatureCollection) => {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  collection.features.forEach((feature) => {
    if (feature.geometry?.type !== "Point") return;
    const [lng, lat] = feature.geometry.coordinates;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });

  if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) {
    return null;
  }

  return new maplibregl.LngLatBounds([minLng, minLat], [maxLng, maxLat]);
};

type HoveredVenueState = {
  id: string;
  name: string;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  geocodeStatus?: string | null;
  capacity?: number | null;
  point: { x: number; y: number };
};

type VenueMapProps = {
  collection: VenueFeatureCollection;
  sourceId: string;
  viewMode: VenueMapView;
  onSelectVenue?: (venueId: string) => void;
  selectedVenueId?: string | null;
};

export const VenueMap = memo(function VenueMap({
  collection,
  sourceId,
  viewMode,
  onSelectVenue,
  selectedVenueId,
}: VenueMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [hoveredVenue, setHoveredVenue] = useState<HoveredVenueState | null>(null);
  const hoveredClusterId = useRef<number | null>(null);
  const lastFitSignature = useRef<string | null>(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) {
      return;
    }

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: 3,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");

    map.on("load", () => {
      setMapReady(true);
    });

    const resizeObserver = new ResizeObserver(() => {
      map.resize();
    });
    resizeObserver.observe(containerRef.current);

    mapRef.current = map;

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const clusterSourceId = `${sourceId}-clustered`;
    const pointSourceId = `${sourceId}-points`;

    if (map.getSource(clusterSourceId) && map.getSource(pointSourceId)) {
      (map.getSource(clusterSourceId) as maplibregl.GeoJSONSource).setData(collection);
      (map.getSource(pointSourceId) as maplibregl.GeoJSONSource).setData(collection);
      return;
    }

    map.addSource(clusterSourceId, {
      type: "geojson",
      data: collection,
      cluster: true,
      clusterRadius: 45,
      clusterMaxZoom: 9,
    });
    map.addSource(pointSourceId, {
      type: "geojson",
      data: collection,
      cluster: false,
    });

    map.addLayer({
      id: HEAT_LAYER_ID,
      type: "heatmap",
      source: pointSourceId,
      maxzoom: 9,
      paint: HEATMAP_PAINT,
    });

    map.addLayer({
      id: CLUSTER_LAYER_ID,
      type: "circle",
      source: clusterSourceId,
      filter: ["has", "point_count"],
      paint: {
        "circle-radius": CLUSTER_RADIUS,
        "circle-color": CLUSTER_COLOR,
        "circle-opacity": 0.85,
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#020617",
      },
    });

    map.addLayer({
      id: CLUSTER_COUNT_LAYER_ID,
      type: "symbol",
      source: clusterSourceId,
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
        "text-size": 12,
      },
      paint: {
        "text-color": "#fefce8",
      },
    });

    map.addLayer({
      id: PIN_LAYER_ID,
      type: "circle",
      source: pointSourceId,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": PIN_RADIUS,
        "circle-color": PIN_COLOR,
        "circle-stroke-color": PIN_STROKE,
        "circle-stroke-width": 1.2,
        "circle-opacity": 0.95,
      },
    });
  }, [collection, mapReady, sourceId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer(CLUSTER_LAYER_ID)) return;
    const clusterSourceId = `${sourceId}-clustered`;

    const clearClusterHover = () => {
      if (hoveredClusterId.current === null) return;
      map.setFeatureState(
        { source: clusterSourceId, id: hoveredClusterId.current },
        { hover: false },
      );
      hoveredClusterId.current = null;
    };

    const setClusterHover = (clusterId: number) => {
      if (hoveredClusterId.current === clusterId) return;
      clearClusterHover();
      hoveredClusterId.current = clusterId;
      map.setFeatureState({ source: clusterSourceId, id: clusterId }, { hover: true });
    };

    const handleClusterMove = (event: maplibregl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = "pointer";
      const feature = event.features?.[0];
      if (!feature) return;
      const clusterId = feature.properties?.cluster_id;
      if (typeof clusterId === "number") {
        setClusterHover(clusterId);
      }
    };

    const handleClusterLeave = () => {
      map.getCanvas().style.cursor = "";
      clearClusterHover();
    };

    const handleClusterClick = (event: maplibregl.MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const clusterId = feature.properties?.cluster_id;
      const coordinates = feature.geometry?.type === "Point" ? feature.geometry.coordinates : null;
      if (typeof clusterId !== "number" || !coordinates) return;
      const source = map.getSource(clusterSourceId) as maplibregl.GeoJSONSource;
      source.getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (error) return;
        map.easeTo({
          center: coordinates as [number, number],
          zoom,
          duration: 600,
        });
      });
    };

    map.on("mouseenter", CLUSTER_LAYER_ID, handleClusterMove);
    map.on("mousemove", CLUSTER_LAYER_ID, handleClusterMove);
    map.on("mouseleave", CLUSTER_LAYER_ID, handleClusterLeave);
    map.on("click", CLUSTER_LAYER_ID, handleClusterClick);

    return () => {
      map.off("mouseenter", CLUSTER_LAYER_ID, handleClusterMove);
      map.off("mousemove", CLUSTER_LAYER_ID, handleClusterMove);
      map.off("mouseleave", CLUSTER_LAYER_ID, handleClusterLeave);
      map.off("click", CLUSTER_LAYER_ID, handleClusterClick);
    };
  }, [mapReady, sourceId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getLayer(PIN_LAYER_ID)) return;

    const handlePinEnter = (event: maplibregl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = "pointer";
      updateHover(event);
    };

    const handlePinMove = (event: maplibregl.MapLayerMouseEvent) => {
      updateHover(event);
    };

    const handlePinLeave = () => {
      map.getCanvas().style.cursor = "";
      setHoveredVenue(null);
    };

    const handlePinClick = (event: maplibregl.MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      if (!feature?.properties?.id) return;
      onSelectVenue?.(String(feature.properties.id));
    };

    function updateHover(event: maplibregl.MapLayerMouseEvent) {
      const feature = event.features?.[0];
      if (!feature) return;
      const properties = feature.properties ?? {};
      setHoveredVenue({
        id: String(properties.id ?? ""),
        name: normalizeUnknownValue(properties.name as string) ?? "Venue",
        city: normalizeUnknownValue(properties.city as string),
        state: normalizeUnknownValue(properties.state as string),
        country: normalizeUnknownValue(properties.country as string),
        geocodeStatus: normalizeUnknownValue(properties.geocodeStatus as string),
        capacity: Number.isFinite(properties.capacity) ? Number(properties.capacity) : null,
        point: { x: event.point.x, y: event.point.y },
      });
    }

    map.on("mouseenter", PIN_LAYER_ID, handlePinEnter);
    map.on("mousemove", PIN_LAYER_ID, handlePinMove);
    map.on("mouseleave", PIN_LAYER_ID, handlePinLeave);
    map.on("click", PIN_LAYER_ID, handlePinClick);

    return () => {
      map.off("mouseenter", PIN_LAYER_ID, handlePinEnter);
      map.off("mousemove", PIN_LAYER_ID, handlePinMove);
      map.off("mouseleave", PIN_LAYER_ID, handlePinLeave);
      map.off("click", PIN_LAYER_ID, handlePinClick);
    };
  }, [mapReady, onSelectVenue]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const showHeat = viewMode === "heat";
    const showClusters = viewMode === "world" || viewMode === "clusters";
    const showPins = viewMode === "world" || viewMode === "pins";

    const setVisibility = (layerId: string, visible: boolean) => {
      if (!map.getLayer(layerId)) return;
      map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
    };

    setVisibility(HEAT_LAYER_ID, showHeat);
    setVisibility(CLUSTER_LAYER_ID, showClusters && !showHeat);
    setVisibility(CLUSTER_COUNT_LAYER_ID, showClusters && !showHeat);
    setVisibility(PIN_LAYER_ID, showPins && !showHeat);

    if (!showPins) {
      setHoveredVenue(null);
    }
  }, [mapReady, viewMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !selectedVenueId) return;
    const match = collection.features.find(
      (feature) => String(feature.properties?.id ?? "") === String(selectedVenueId),
    );
    if (!match || match.geometry?.type !== "Point") return;
    const [lng, lat] = match.geometry.coordinates;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    map.easeTo({
      center: [lng, lat],
      zoom: Math.max(map.getZoom(), 7.5),
      duration: 700,
    });
  }, [collection.features, mapReady, selectedVenueId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const bounds = getBoundsFromCollection(collection);
    if (!bounds) return;
    const signature = `${collection.features.length}:${bounds.toArray().join("|")}`;
    if (lastFitSignature.current === signature) return;
    lastFitSignature.current = signature;
    map.fitBounds(bounds, { padding: 48, maxZoom: 8, duration: 700 });
  }, [collection, mapReady]);

  const tooltipAnchor = useMemo(() => {
    if (!hoveredVenue) return { left: -9999, top: -9999 };
    return {
      left: hoveredVenue.point.x,
      top: hoveredVenue.point.y,
    };
  }, [hoveredVenue]);

  const tooltipOpen = Boolean(hoveredVenue && (viewMode === "world" || viewMode === "pins"));

  return (
    <div className="relative h-full w-full">
      <div
        ref={containerRef}
        className="h-full w-full overflow-hidden rounded-2xl border border-border bg-background/40 shadow-2xl"
      />
      <TooltipProvider delayDuration={0}>
        <Tooltip open={tooltipOpen}>
          <TooltipTrigger asChild>
            <span
              className="pointer-events-none absolute h-2 w-2"
              style={{
                left: tooltipAnchor.left,
                top: tooltipAnchor.top,
                transform: "translate(-50%, -120%)",
              }}
            />
          </TooltipTrigger>
          {hoveredVenue ? (
            <TooltipContent className="bg-background/85 text-foreground">
              <p className="text-sm font-semibold">{hoveredVenue.name}</p>
              <p className="text-xs text-muted-foreground">
                {[hoveredVenue.city, hoveredVenue.state, hoveredVenue.country]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              {hoveredVenue.geocodeStatus && hoveredVenue.geocodeStatus !== "precise" ? (
                <p className="mt-1 text-xs text-rose-200">Approximate geocode</p>
              ) : null}
              {hoveredVenue.capacity ? (
                <p className="text-xs text-muted-foreground">
                  Capacity {hoveredVenue.capacity.toLocaleString()}
                </p>
              ) : null}
            </TooltipContent>
          ) : null}
        </Tooltip>
      </TooltipProvider>
    </div>
  );
});
